import { fmtDate } from '../utils';
import { useState, useEffect } from 'react';
import { api } from '../api';

const MONTHS = ['January','February','March','April','May','June',
                 'July','August','September','October','November','December'];

function fullName(t) { return t ? `${t.firstName || ''} ${t.lastName || ''}`.trim() : ''; }

export default function Salaries() {
  const [tab, setTab] = useState('payroll');
  const [teachers, setTeachers] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  // Pay form — field names match Salary entity JSON properties
  const [form, setForm] = useState({
    teacher: { id: '' },
    payMonth: new Date().getMonth() + 1,
    payYear: new Date().getFullYear(),
    basic: '', hra: '0', allowances: '0', deductions: '0', bonus: '0', netSalary: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Bank Transfer', status: 'Paid'
  });
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getTeachers().then(setTeachers).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.getPayroll(month, year).then(setSalaries);
  }, [month, year]);

  const handleTeacherChange = (tid) => {
    const t = teachers.find(t => t.id === parseInt(tid));
    const basic = t?.basicSalary || 0;
    setForm(f => ({
      ...f,
      teacher: { id: tid },
      basic: basic,
      netSalary: (parseFloat(basic) + parseFloat(f.hra || 0) + parseFloat(f.allowances || 0)
        + parseFloat(f.bonus || 0) - parseFloat(f.deductions || 0)).toFixed(2)
    }));
  };

  const recalc = (f) => ({
    ...f,
    netSalary: (
      parseFloat(f.basic || 0) + parseFloat(f.hra || 0) +
      parseFloat(f.allowances || 0) + parseFloat(f.bonus || 0) -
      parseFloat(f.deductions || 0)
    ).toFixed(2)
  });

  const set = (k, v) => setForm(f => recalc({ ...f, [k]: v }));

  const handlePay = async () => {
    if (!form.teacher.id || !form.basic) { setErr('Teacher and basic salary required'); return; }
    try {
      await api.createSalary({
        ...form,
        teacher: { id: parseInt(form.teacher.id) },
        basic: parseFloat(form.basic),
        hra: parseFloat(form.hra || 0),
        allowances: parseFloat(form.allowances || 0),
        deductions: parseFloat(form.deductions || 0),
        bonus: parseFloat(form.bonus || 0),
        netSalary: parseFloat(form.netSalary)
      });
      window.location.reload();
    } catch (e) { setErr(e.message); }
  };

  const totalNet = salaries.reduce((s, sal) => s + parseFloat(sal.netSalary || 0), 0);

  if (loading) return <div className="loading-msg">Loading…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'payroll' ? ' active' : ''}`} onClick={() => setTab('payroll')}>Payroll Register</button>
        <button className={`tab${tab === 'pay' ? ' active' : ''}`} onClick={() => setTab('pay')}>Process Salary</button>
      </div>

      {tab === 'payroll' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Filter Period</div>
            </div>
            <div className="card-body">
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="form-label">Month</label>
                  <select className="form-control" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
                    {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Year</label>
                  <input className="form-control" type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} />
                </div>
              </div>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon green"><i className="fas fa-wallet"></i></div>
              <div>
                <div className="stat-label">Total Payroll — {MONTHS[month-1]} {year}</div>
                <div className="stat-value">₹{totalNet.toLocaleString('en-IN')}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon blue"><i className="fas fa-chalkboard-teacher"></i></div>
              <div>
                <div className="stat-label">Teachers Paid</div>
                <div className="stat-value">{salaries.filter(s => s.status?.toLowerCase() === 'paid').length}</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Salary Records</div>
              <div className="card-subtitle">{salaries.length} entries</div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>#</th><th>Teacher</th><th>Basic</th><th>HRA</th><th>Allowances</th><th>Deductions</th><th>Net Salary</th><th>Mode</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {salaries.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 30 }}>No salary records for this period</td></tr>
                  )}
                  {salaries.map((s, i) => (
                    <tr key={s.id}>
                      <td>{i + 1}</td>
                      <td><strong>{fullName(s.teacher)}</strong></td>
                      <td>₹{Number(s.basic || 0).toLocaleString('en-IN')}</td>
                      <td><span className="badge badge-green">+₹{Number(s.hra || 0).toLocaleString('en-IN')}</span></td>
                      <td><span className="badge badge-green">+₹{Number(s.allowances || 0).toLocaleString('en-IN')}</span></td>
                      <td><span className="badge badge-red">-₹{Number(s.deductions || 0).toLocaleString('en-IN')}</span></td>
                      <td><strong>₹{Number(s.netSalary || 0).toLocaleString('en-IN')}</strong></td>
                      <td style={{ textTransform: 'capitalize' }}>{s.paymentMode}</td>
                      <td>
                        <span className={`badge ${s.status?.toLowerCase() === 'paid' ? 'badge-green' : 'badge-orange'}`}>{s.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'pay' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Process Salary Payment</div>
          </div>
          <div className="card-body">
            {err && <div className="error-msg">{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Teacher</label>
                <select className="form-control" value={form.teacher.id} onChange={e => handleTeacherChange(e.target.value)}>
                  <option value="">-- Select Teacher --</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{fullName(t)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Month</label>
                <select className="form-control" value={form.payMonth} onChange={e => set('payMonth', parseInt(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Year</label>
                <input className="form-control" type="number" value={form.payYear} onChange={e => set('payYear', parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-label">Basic (₹)</label>
                <input className="form-control" type="number" value={form.basic} onChange={e => set('basic', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">HRA (₹)</label>
                <input className="form-control" type="number" value={form.hra} onChange={e => set('hra', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Other Allowances (₹)</label>
                <input className="form-control" type="number" value={form.allowances} onChange={e => set('allowances', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Bonus (₹)</label>
                <input className="form-control" type="number" value={form.bonus} onChange={e => set('bonus', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Deductions (₹)</label>
                <input className="form-control" type="number" value={form.deductions} onChange={e => set('deductions', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Net Salary (₹)</label>
                <input className="form-control" type="number" value={form.netSalary} readOnly style={{ background: '#f5f7fa' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date</label>
                <input className="form-control" type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Mode</label>
                <select className="form-control" value={form.paymentMode} onChange={e => set('paymentMode', e.target.value)}>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-success" onClick={handlePay}>
                <i className="fas fa-wallet"></i> Process Payment
              </button>
              {saved && <span className="badge badge-green">Salary processed!</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
